import React, { createContext, useCallback, useContext, useState } from 'react';

type ApplyFn = (relPath: string, value: any) => void;

export type SectionEntry = {
  id: string;
  label: string;
  basePath: string;
  fieldsToCopy?: string[];
  applyFn: ApplyFn;
};

type SectionRegistryContextValue = {
  register: (entry: Omit<SectionEntry, 'id'>) => string;
  unregister: (id: string) => void;
  list: () => SectionEntry[];
};

const SectionRegistryContext = createContext<SectionRegistryContextValue | null>(null);

export const SectionRegistryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [map, setMap] = useState<Record<string, SectionEntry>>({});

  const register = useCallback((entry: Omit<SectionEntry, 'id'>) => {
    const id = `${entry.basePath}::${Math.random().toString(36).slice(2, 9)}`;
    setMap((s) => ({ ...s, [id]: { ...entry, id } }));
    return id;
  }, []);

  const unregister = useCallback((id: string) => {
    setMap((s) => {
      const copy = { ...s };
      delete copy[id];
      return copy;
    });
  }, []);

  const list = useCallback(() => Object.values(map), [map]);

  return (
    <SectionRegistryContext.Provider value={{ register, unregister, list }}>{children}</SectionRegistryContext.Provider>
  );
};

export const useSectionRegistry = () => {
  const ctx = useContext(SectionRegistryContext);
  if (!ctx) throw new Error('useSectionRegistry must be used within SectionRegistryProvider');
  return ctx;
};

export default SectionRegistryContext;
